import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { Group } from '../../../models/group';
import { GroupService } from '../../../services/group.service';
import { Tournament } from '../../../models/tournament';
import { TournamentService } from '../../../services/tournament.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { LogService } from '../../../services/log.service';
import _ from 'lodash';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { Team } from '../../../models/team';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { CommonModule } from '@angular/common';
import { TeamService } from '../../../services/team.service';
import { RoundService } from '../../../services/round.service';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-tournament-detail-groups',
  imports: [
    ButtonModule,
    CommonModule,
    ConfirmDialogModule,
    ConfirmPopupModule,
    MessageModule,
    Popover,
    PopoverModule,
    TableModule,
    TooltipModule,
    TranslateModule,
  ],
  providers: [
    ConfirmationService,
  ],
  templateUrl: './tournament-detail-groups.component.html',
  styleUrl: './tournament-detail-groups.component.scss'
})
export class TournamentDetailGroupsComponent implements OnInit {
  @ViewChild('moveTeamPop', { static: true }) moveTeamPop!: Popover;

  public _ = _;
  private readonly destroy$ = inject(DestroyRef);
  public filter = computed(() => this.auxiliarService.searchField());
  private groupOrigin: Group | undefined;
  public groups: Group[] = [];
  public groupsAvailable: Group[] = [];
  public loading: boolean = false;
  public loadingRounds: boolean = false;
  private teamToMove: Team | undefined;
  public tournament: Tournament | undefined;

  constructor(
    public authService: AuthService,
    private auxiliarService: AuxiliarService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
    private groupService: GroupService,
    private logService: LogService,
    private roundService: RoundService,
    private router: Router,
    private teamService: TeamService,
    private tournamentService: TournamentService,
    private translate: TranslateService,
  ){}

  ngOnInit(): void {
    this.tournament = this.tournamentService.tournament();
    this.getTournamentGroups();
  }

  private getTournamentGroups(): void {
    if(!this.tournament?.id) return;
    this.groupService.getTournamentGroups(this.tournament.id)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (res) => {
          this.logService.info('[Group] - getTournamentGroups', _.get(res, 'body'));
          this.groups = _.orderBy(_.map(_.get(res, 'body.data', []), (group: Group) => {
            return {...group, name: `${this.translate.instant('field.group')} ${group.name}`}
          }), 'name');
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.logService.error('[Group] - getTournamentGroups', err, this.translate.instant('error.tournament-groups'));
        }
      });
  }

  public checkGroupsDifference(): boolean {
    const groupsTeamsCount: number[] = _.map(this.groups, (g) => g.teams?.length || 0);
    return (_.max(groupsTeamsCount) || 0) - (_.min(groupsTeamsCount) || 0) <= 1;
  }

  public checkGroupDifference(group: Group): boolean {
    const groupsTeamsCount: number[] = _.map(this.groups, (g) => g.teams?.length || 0);
    return ((_.max(groupsTeamsCount) || 0) - (group.teams?.length || 0) > 1) || 
      ((group.teams?.length || 0) - (_.min(groupsTeamsCount) || 0) > 1);
  }

  public toggleMoveTeamPop(event: Event, team: Team, group: Group): void {
    this.teamToMove = team;
    this.groupOrigin = group;
    // get available groups
    this.groupsAvailable = _.filter(this.groups, (g) => g.id !== this.groupOrigin?.id);
    // open pop up
    this.moveTeamPop.toggle(event);
  }

  public moveGroup(groupDestiny: Group | undefined): void {
    this.moveTeamPop.hide();
    let teamData = _.cloneDeep(this.teamToMove) as Team;
    _.set(teamData, 'groupId', groupDestiny?.id);
    this.teamService.updateTeam(teamData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
      next: (res) => {
        this.logService.info('[Team] - updateTeam', res);
        if(this.teamToMove && this.groupOrigin && groupDestiny) {
          groupDestiny?.teams?.push(this.teamToMove);
          _.remove(this.groupOrigin?.teams || [], {id: this.teamToMove.id});
        }
      },
      error: (err) => {
        this.logService.error('[Team] - updateTeam', err, this.translate.instant('error.team-update'));
      }
    });
  }

  public createRounds(event: Event): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant('confirmation.rounds-create'),
      header: '',
      icon: 'pi pi-info-circle',
      closable: false,
      key: 'rounds-create',
      accept: () => {
        if(!this.tournament?.id) return;
        this.loadingRounds = true;
        this.roundService.createTournamentRounds(this.tournament.id)
          .pipe(
            takeUntilDestroyed(this.destroy$),
            finalize(() => {
              this.loadingRounds = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe({
            next: (res) => {
              this.logService.debug('[Round] - createTournamentRounds', res);
              if(this.tournament?.id) this.tournamentService.getTournament(this.tournament.id).subscribe(() => {
                this.router.navigate(['admin', 'tournaments', this.tournament?.id, 'rounds']);
                this.tournamentService.setTournamentSection('rounds');
              });
            },
            error: (err) => {
              this.logService.error('[Round] - createTournamentRounds', err, this.translate.instant('error.rounds-create'));
            }
          });
      },
      reject: () => {}
    });
  }
}
